import FirebaseAdapter from "@wc/adapters/firebase";

/**
 * @typedef { import('@wc/adapters/firebase/db-models').NoSQLTeamEvent } NoSQLTeamEvent
 */

/**
 * Create a goal locked-in event
 * @param {string} teamId The team id
 * @param {string} userId User who locked in
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @returns {Promise<NoSQLTeamEvent>} The created event
 */
export async function createGoalLockedEvent(teamId, userId, weekOf) {
  return await FirebaseAdapter().events.createTeamEvent(teamId, {
    type: "goal_locked_in",
    userId,
    weekOf,
    metadata: {},
  });
}

/**
 * Create a goal unlocked event
 * @param {string} teamId The team id
 * @param {string} userId User who unlocked
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @returns {Promise<NoSQLTeamEvent>} The created event
 */
export async function createGoalUnlockedEvent(teamId, userId, weekOf) {
  return await FirebaseAdapter().events.createTeamEvent(teamId, {
    type: "goal_unlocked",
    userId,
    weekOf,
    metadata: {},
  });
}

/**
 * Create a goal checked (completed) event
 * @param {string} teamId The team id
 * @param {string} userId User who checked the goal
 * @param {string} goalId The goal id
 * @param {string} goalText The goal text
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @returns {Promise<NoSQLTeamEvent>} The created event
 */
export async function createGoalCheckedEvent(
  teamId,
  userId,
  goalId,
  goalText,
  weekOf,
) {
  return await FirebaseAdapter().events.createTeamEvent(teamId, {
    type: "goal_checked",
    userId,
    goalId,
    weekOf,
    metadata: {
      goalText,
    },
  });
}

/**
 * Create a goal unchecked event
 * @param {string} teamId The team id
 * @param {string} userId User who unchecked the goal
 * @param {string} goalId The goal id
 * @param {string} goalText The goal text
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @returns {Promise<NoSQLTeamEvent>} The created event
 */
export async function createGoalUncheckedEvent(
  teamId,
  userId,
  goalId,
  goalText,
  weekOf,
) {
  return await FirebaseAdapter().events.createTeamEvent(teamId, {
    type: "goal_unchecked",
    userId,
    goalId,
    weekOf,
    metadata: {
      goalText,
    },
  });
}

/**
 * Create an all goals complete event
 * @param {string} teamId The team id
 * @param {string} userId User who completed all goals
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @returns {Promise<NoSQLTeamEvent>} The created event
 */
export async function createAllGoalsCompleteEvent(teamId, userId, weekOf) {
  return await FirebaseAdapter().events.createTeamEvent(teamId, {
    type: "all_goals_complete",
    userId,
    weekOf,
    metadata: {
      goalCount: 5,
    },
  });
}

/**
 * Create a comment event
 * @param {string} teamId The team id
 * @param {string} userId User who is commenting
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @param {string} text The comment text (max 100 chars)
 * @returns {Promise<NoSQLTeamEvent>} The created event
 */
export async function createCommentEvent(teamId, userId, weekOf, text) {
  return await FirebaseAdapter().events.createTeamEvent(teamId, {
    type: "comment",
    userId,
    weekOf,
    metadata: {
      text: text.slice(0, 100),
    },
  });
}

/**
 * Get team events with user details populated
 * @param {string} teamId The team id
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @returns {Promise<Array<NoSQLTeamEvent & {user: object}>>} Events with user info
 */
export async function getTeamEventsWithUsers(teamId, weekOf) {
  const events = await FirebaseAdapter().events.listTeamEvents(teamId, weekOf);

  // Get unique user IDs
  const userIds = [...new Set(events.map((e) => e.userId))];

  // Fetch user details
  const users = await Promise.all(
    userIds.map((userId) => FirebaseAdapter().users.getUserById(userId)),
  );

  // Create user map for quick lookup
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  // Attach user info to events
  return events.map((event) => ({
    ...event,
    user: {
      id: event.userId,
      displayName: userMap[event.userId]?.profile?.displayName || "Unknown",
      photoURL: userMap[event.userId]?.profile?.photoURL || null,
    },
  }));
}
